import React from 'react';

import { AppIcon, type AppIcons } from '@backrs_ui/common-ui';

import { Box, SxProps, Theme, Typography } from '@mui/material';

export type GuideStrategy = Readonly<{
  id: string;
  title: string;
  points: readonly string[];
}>;

type GuideStrategyByGoalSectionProps = Readonly<{
  strategies: readonly GuideStrategy[];
  iconMap: Record<string, AppIcons>;
  columns: 2 | 3;
}>;

const BoldBeforeColon: React.FC<{ text: string }> = ({ text }) => {
  const colonIndex = text.indexOf(':');

  if (colonIndex === -1) return <>{text}</>;

  return (
    <>
      <strong>{text.slice(0, colonIndex + 1)}</strong>
      {text.slice(colonIndex + 1)}
    </>
  );
};

export const GuideStrategyByGoalSection: React.FC<GuideStrategyByGoalSectionProps> = ({
  strategies,
  iconMap,
  columns,
}) => (
  <Box sx={containerSx}>
    <Box sx={gridSx(columns)}>
      {strategies.map((strategy) => (
        <Box key={strategy.id} sx={columnSx}>
          <Box sx={titleRowSx}>
            <AppIcon
              iconName={iconMap[strategy.id] ?? 'ArrowRight'}
              diameter="20px"
              color="Blue/500"
            />
            <Typography variant="text-md-semibold" sx={titleSx}>
              {strategy.title}
            </Typography>
          </Box>
          <Box sx={listSx}>
            {strategy.points.map((point) => (
              <Typography key={point} variant="text-sm-regular" sx={bodyTextSx}>
                <BoldBeforeColon text={point} />
              </Typography>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  pb: backrs.spacing['spacing-4xl'],
});

const gridSx =
  (columns: 2 | 3): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: backrs.spacing['spacing-3xl'],
    '@container (min-width: 720px)': {
      gridTemplateColumns: columns === 3 ? '1fr 1fr 1fr' : '1fr 1fr',
    },
  });

const columnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Blue/700'],
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const bodyTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});
